import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Building2, Plus, Trash2, Star, AlertCircle, Package, Tag, ChevronDown, ChevronRight,
  DollarSign, Hash, Box, Calendar, MessageSquare, Truck, Boxes
} from 'lucide-react';
import OfferSection from '../forms/OfferSection';
import api from '../../lib/api';
import { toast } from 'sonner';

// ═══════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════

const NATURE_OFFRE_OPTIONS = [
  { value: 'NONE', label: 'Aucune' },
  { value: 'PERMANENTE', label: 'Permanente' },
  { value: 'PRECOMMANDE', label: 'Précommande' },
  { value: 'LANCEMENT', label: 'Lancement' },
  { value: 'PROMOTION', label: 'Promotion' },
  { value: 'SOLDES', label: 'Soldes' },
  { value: 'PROMO_FLASH', label: 'Promo flash' },
  { value: 'DESTOCKAGE', label: 'Déstockage' },
  { value: 'EVENEMENTIELLE', label: 'Événementielle' }
];

// ═══════════════════════════════════════════════════════════
// SINGLE DISTRIBUTION ACCORDION PANEL
// ═══════════════════════════════════════════════════════════

const DistPanel = forwardRef(({ dist, idx, isExpanded, onToggle, isPrimary, partners, onUpdate, onBatchUpdate, onDelete, onSetPrimary, savingId, onDirty, hideControls = false }, ref) => {
  const partnerName = partners.find(p => p.id === dist.partner_id)?.nom || dist.partner_id?.substring(0, 8) + '...';
  const dbMechanics = Array.isArray(dist.promotional_mechanics) ? dist.promotional_mechanics : [];
  const activeMechCount = dbMechanics.filter(m => m.status === 'ACTIVE').length;

  // LOCAL mechanics state — changes accumulate, saved via parent's global save
  const [localMechanics, setLocalMechanics] = useState(dbMechanics);
  const [mechDirty, setMechDirty] = useState(false);
  const offerRef = useRef(null);

  // Sync from parent when dist changes externally
  useEffect(() => {
    const fresh = Array.isArray(dist.promotional_mechanics) ? dist.promotional_mechanics : [];
    setLocalMechanics(fresh);
    setMechDirty(false);
  }, [dist.promotional_mechanics]);

  const isPendingDist = dist.id?.startsWith('pending-');

  const handleMechChange = (newMechs) => {
    setLocalMechanics(newMechs);
    setMechDirty(true);
    onDirty?.();
    // Pending mode (création) : propager vers pendingDistributions immédiatement
    // pour que le payload final contienne les mécaniques.
    // Live mode (édition) : accumulation locale, sauvegarde batch au submit.
    if (isPendingDist) {
      onBatchUpdate(dist.id, { promotional_mechanics: newMechs });
    }
  };

  // Exposed via ref: validate + save mechanics. Returns true if OK, false if validation fails.
  useImperativeHandle(ref, () => ({
    async validateAndSaveMechanics() {
      // If no mechanics and no changes, skip
      if (localMechanics.length === 0 && !mechDirty) return true;
      // Validate
      if (localMechanics.length > 0 && offerRef.current?.validate) {
        if (!offerRef.current.validate()) return false;
      }
      // Save if dirty
      if (mechDirty) {
        try {
          await onBatchUpdate(dist.id, { promotional_mechanics: localMechanics });
          setMechDirty(false);
        } catch {
          return false; // Backend rejected — already toasted by handleBatchUpdate
        }
      }
      return true;
    },
    get hasDirtyMechanics() { return mechDirty; }
  }), [localMechanics, mechDirty, dist.id, onBatchUpdate]);

  const localSet = (field, val) => {
    if (field === 'promotional_mechanics') {
      handleMechChange(val);
      return;
    }
    onUpdate(dist.id, field, val);
    onDirty?.();
  };

  return (
    <div className="border-l-4 border-l-indigo-400 border rounded-lg overflow-hidden bg-white" data-testid={`dist-accordion-${idx}`}>
      {/* === ACCORDION HEADER === */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onToggle(idx)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(idx); } }}
        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors cursor-pointer ${isPrimary ? 'bg-yellow-50/60 hover:bg-yellow-50' : 'bg-gray-50 hover:bg-gray-100'}`}
        data-testid={`dist-accordion-toggle-${idx}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />}
          <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-900 truncate">{partnerName}</span>
          {isPrimary && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-[10px] font-medium rounded-full flex-shrink-0">
              <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" /> Principal
            </span>
          )}
          {activeMechCount > 0 && (
            <span className="text-[10px] text-indigo-500 flex-shrink-0">{activeMechCount} méca.</span>
          )}
          {dist.purchase_price != null && (
            <span className="text-xs text-gray-500 flex-shrink-0">{Number(dist.purchase_price).toFixed(2)} &euro;</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
          {!hideControls && !isPrimary && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-gray-500 hover:text-yellow-700" onClick={() => onSetPrimary(dist.partner_id)} data-testid={`set-primary-${idx}`}>
              <Star className="h-3 w-3 mr-1" /> Principal
            </Button>
          )}
          {!hideControls && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-red-500" onClick={() => onDelete(dist.id, dist.partner_id)} data-testid={`delete-dist-${idx}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* === ACCORDION BODY === */}
      {isExpanded && (
        <div className="px-4 py-4 space-y-5 border-t border-gray-100">

          {/* ── LOGISTIQUE ── */}
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Truck className="h-3.5 w-3.5" /> Logistique et Supply
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Prix d&apos;achat HT</Label>
                <Input type="number" step="0.01" placeholder="--" value={dist.purchase_price ?? ''}
                  onChange={(e) => localSet('purchase_price', e.target.value === '' ? null : parseFloat(e.target.value))}
                  className="h-8 text-sm" data-testid={`dist-price-${idx}`} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Réf. fournisseur</Label>
                <Input placeholder="--" value={dist.supplier_reference || ''}
                  onChange={(e) => localSet('supplier_reference', e.target.value || null)}
                  className="h-8 text-sm" data-testid={`dist-ref-${idx}`} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Disponibilité</Label>
                <select value={dist.availability_status || 'unknown'}
                  onChange={(e) => localSet('availability_status', e.target.value)}
                  className="w-full h-8 px-2 rounded-md border border-input bg-background text-sm" data-testid={`dist-status-${idx}`}>
                  <option value="unknown">Inconnu</option>
                  <option value="available">Disponible</option>
                  <option value="unavailable">Indisponible</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs text-gray-500">Date retour stock</Label>
                <Input type="date" value={(dist.date_disponibilite_iso || '').slice(0, 10)}
                  onChange={(e) => localSet('date_disponibilite_iso', e.target.value || null)}
                  className="h-8 text-sm" data-testid={`dist-date-dispo-${idx}`} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Commentaire dispo</Label>
                <Input placeholder="--" value={dist.commentaire_disponibilite || ''}
                  onChange={(e) => localSet('commentaire_disponibilite', e.target.value || null)}
                  className="h-8 text-sm" data-testid={`dist-comment-dispo-${idx}`} />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
              <div>
                <Label className="text-xs text-gray-500">Min. commande</Label>
                <Input type="number" min="0" placeholder="--" value={dist.minimum_commande ?? ''}
                  onChange={(e) => localSet('minimum_commande', e.target.value === '' ? null : parseInt(e.target.value))}
                  className="h-8 text-sm" data-testid={`dist-min-${idx}`} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Max. commande</Label>
                <Input type="number" min="0" placeholder="--" value={dist.maximum_commande ?? ''}
                  onChange={(e) => localSet('maximum_commande', e.target.value === '' ? null : parseInt(e.target.value))}
                  className="h-8 text-sm" data-testid={`dist-max-${idx}`} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Multiple</Label>
                <Input type="number" min="1" placeholder="--" value={dist.multiple_commande ?? ''}
                  onChange={(e) => localSet('multiple_commande', e.target.value === '' ? null : parseInt(e.target.value))}
                  className="h-8 text-sm" data-testid={`dist-multiple-${idx}`} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Colisage</Label>
                <Input type="number" min="1" placeholder="--" value={dist.colisage ?? ''}
                  onChange={(e) => localSet('colisage', e.target.value === '' ? null : parseInt(e.target.value))}
                  className="h-8 text-sm" data-testid={`dist-colisage-${idx}`} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs text-gray-500">Dimension carton</Label>
                <Input value={dist.dimension_carton || ''} placeholder="30x20x10 cm"
                  onChange={(e) => localSet('dimension_carton', e.target.value || null)}
                  className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Cartons/palette</Label>
                <Input type="number" min="1" value={dist.nombre_carton_par_palette ?? ''}
                  onChange={(e) => localSet('nombre_carton_par_palette', e.target.value === '' ? null : parseInt(e.target.value))}
                  className="h-8 text-sm" />
              </div>
            </div>
          </div>

          {/* ── CONDITIONS D'ACHAT ── */}
          <div className="border-t border-gray-100 pt-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5" /> Conditions d&apos;achat
            </p>

            {/* ═══ CONDITIONS D'ACHAT ═══ */}
            <OfferSection
              ref={offerRef}
              mechanics={localMechanics}
              onChange={handleMechChange}
            />
          </div>

          {savingId === dist.id && (
            <div className="text-xs text-indigo-500 flex items-center gap-1">
              <div className="animate-spin h-3 w-3 border border-indigo-400 border-t-transparent rounded-full" /> Sauvegarde...
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

/**
 * AdminDistributionsManager V3 — Accordion UX + Pending mode
 *
 * Props:
 * @param {string|null} productId - null = pending mode (creation)
 * @param {string} currentPartnerId
 * @param {Array} partners
 * @param {Function} onPrimaryChange
 * @param {Array} pendingDistributions - (creation mode) external state
 * @param {Function} onPendingChange - (creation mode) setter for pendingDistributions
 */
const AdminDistributionsManager = forwardRef(({
  productId, currentPartnerId, partners = [], onPrimaryChange,
  pendingDistributions, onPendingChange, onDirty,
  partnerMode = false  // Partner mode: single distribution, no dropdown, auto-create
}, managerRef) => {
  const isPending = !productId; // Creation mode = no productId
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(!isPending);
  const [adding, setAdding] = useState(false);
  const [newPartner, setNewPartner] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(0); // First open by default
  const distPanelRefs = useRef({});

  const activePartners = partners.filter(p => p.is_active !== false);

  // === LIVE MODE: load from API ===
  const loadDistributions = useCallback(async () => {
    if (isPending) return;
    try {
      const res = await api.get(`/products/${productId}/distributions`);
      setDistributions(res.data || []);
    } catch {
      setDistributions([]);
    } finally {
      setLoading(false);
    }
  }, [productId, isPending]);

  useEffect(() => { loadDistributions(); }, [loadDistributions]);

  // Partner mode: auto-create distribution if none exists
  const autoCreateAttempted = useRef(false);
  useEffect(() => {
    if (!partnerMode || isPending || loading || autoCreateAttempted.current) return;
    if (distributions.length === 0 && currentPartnerId && productId) {
      autoCreateAttempted.current = true;
      (async () => {
        try {
          await api.post(`/products/${productId}/distributions`, {
            product_id: productId,
            partner_id: currentPartnerId,
            purchase_price: null,
            availability_status: 'unknown'
          });
          await loadDistributions();
        } catch {
          // Distribution may already exist or creation failed
          setLoading(false);
        }
      })();
    }
  }, [partnerMode, isPending, loading, distributions.length, currentPartnerId, productId, loadDistributions]);

  // In pending mode, distributions come from parent
  const effectiveDists = useMemo(() => isPending ? (pendingDistributions || []) : distributions, [isPending, pendingDistributions, distributions]);
  const setEffective = useCallback(
    (fn) => {
      if (isPending) {
        onPendingChange?.(typeof fn === 'function' ? fn(pendingDistributions || []) : fn);
      } else {
        setDistributions(fn);
      }
    },
    [isPending, onPendingChange, pendingDistributions]
  );

  const isDuplicate = useCallback((partnerId) => effectiveDists.some(d => d.partner_id === partnerId), [effectiveDists]);

  const primaryPartnerId = effectiveDists.length > 0
    ? (effectiveDists.find(d => d.partner_id === currentPartnerId)?.partner_id || effectiveDists[0].partner_id)
    : currentPartnerId;

  // === ADD ===
  const handleAdd = async () => {
    if (!newPartner || isDuplicate(newPartner)) return;

    if (isPending) {
      // Pending mode: add to local state with temp id
      const tempDist = {
        id: `pending-${Date.now()}`,
        partner_id: newPartner,
        purchase_price: null, supplier_reference: null,
        availability_status: 'unknown', date_disponibilite_iso: null,
        commentaire_disponibilite: null,
        minimum_commande: null, maximum_commande: null,
        multiple_commande: null, colisage: null,
        promotional_mechanics: [],
        dimension_carton: null, nombre_carton_par_palette: null
      };
      setEffective(prev => [...prev, tempDist]);
      setNewPartner('');
      setExpandedIdx(effectiveDists.length); // expand newly added
      return;
    }

    setAdding(true);
    try {
      await api.post(`/products/${productId}/distributions`, {
        product_id: productId, partner_id: newPartner,
        purchase_price: null, availability_status: 'unknown'
      });
      setNewPartner('');
      toast.success('Distributeur ajouté');
      await loadDistributions();
      if (effectiveDists.length === 0 && !currentPartnerId) onPrimaryChange?.(newPartner);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setAdding(false);
    }
  };

  // === DELETE ===
  const handleDelete = async (distId, partnerId) => {
    if (isPending) {
      setEffective(prev => prev.filter(d => d.id !== distId));
      return;
    }
    try {
      await api.delete(`/products/${productId}/distributions/${distId}`);
      toast.success('Distributeur retiré');
      const remaining = distributions.filter(d => d.id !== distId);
      setDistributions(remaining);
      if (partnerId === primaryPartnerId && remaining.length > 0) onPrimaryChange?.(remaining[0].partner_id);
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  // === UPDATE single field (live mode: debounce-save, pending: local only) ===
  const handleUpdateField = useCallback(async (distId, field, value) => {
    // Always update local state immediately
    setEffective(prev => prev.map(d => d.id === distId ? { ...d, [field]: value } : d));
    if (isPending) return;
    // Live mode: save to API with debounce
    setSavingId(distId);
    try {
      await api.put(`/products/${productId}/distributions/${distId}`, { [field]: value });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSavingId(null);
    }
  }, [isPending, productId, setEffective]);

  // === BATCH UPDATE ===
  const handleBatchUpdate = useCallback(async (distId, fieldsObj) => {
    setEffective(prev => prev.map(d => d.id === distId ? { ...d, ...fieldsObj } : d));
    if (isPending) return;
    setSavingId(distId);
    try {
      await api.put(`/products/${productId}/distributions/${distId}`, fieldsObj);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
      throw err; // Re-throw so validateAndSaveMechanics can catch it
    } finally {
      setSavingId(null);
    }
  }, [isPending, productId, setEffective]);

  // === SET PRIMARY ===
  const handleSetPrimary = async (partnerId) => {
    if (partnerId === primaryPartnerId) return;
    if (isPending) { onPrimaryChange?.(partnerId); return; }
    try {
      await api.put(`/products/${productId}`, { partner_id: partnerId });
      onPrimaryChange?.(partnerId);
      toast.success('Distributeur principal mis à jour');
    } catch { toast.error('Erreur'); }
  };

  const availablePartners = activePartners.filter(p => !isDuplicate(p.id));

  // Expose validateAndSaveAllMechanics to parent (AdminProducts)
  useImperativeHandle(managerRef, () => ({
    async validateAndSaveAllMechanics() {
      const distIds = effectiveDists.map(d => d.id);
      for (const distId of distIds) {
        const panelRef = distPanelRefs.current[distId];
        if (panelRef?.validateAndSaveMechanics) {
          const ok = await panelRef.validateAndSaveMechanics();
          if (!ok) return false;
        }
      }
      return true;
    }
  }), [effectiveDists]);

  if (loading) {
    return (
      <div className="p-4 border rounded-lg bg-gray-50">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
          Chargement des distributeurs...
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid="admin-distributions-manager">
      {/* Header */}
      <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-indigo-600" />
          <span className="text-sm font-semibold text-indigo-800">
            {partnerMode ? 'Ma Distribution' : `Distributeurs (${effectiveDists.length})`}
          </span>
          {isPending && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">Brouillon</span>}
        </div>
        {!partnerMode && effectiveDists.length > 1 && (
          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">Multi-sourcing</span>
        )}
      </div>

      {/* Accordion panels */}
      <div className="p-3 space-y-2">
        {effectiveDists.length === 0 && (
          <div className="py-6 text-center text-sm text-gray-400">
            <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            Aucun distributeur. Ajoutez-en un ci-dessous.
          </div>
        )}

        {effectiveDists.map((dist, idx) => (
          <DistPanel
            key={dist.id}
            ref={(el) => { distPanelRefs.current[dist.id] = el; }}
            dist={dist}
            idx={idx}
            isExpanded={expandedIdx === idx}
            onToggle={(i) => setExpandedIdx(expandedIdx === i ? -1 : i)}
            isPrimary={dist.partner_id === primaryPartnerId}
            partners={partners}
            onUpdate={handleUpdateField}
            onBatchUpdate={handleBatchUpdate}
            onDelete={handleDelete}
            onSetPrimary={handleSetPrimary}
            savingId={savingId}
            onDirty={onDirty}
            hideControls={partnerMode}
          />
        ))}
      </div>

      {/* Add new — masqué en mode Partner (single distribution, auto-créée) */}
      {!partnerMode && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          {availablePartners.length === 0 && effectiveDists.length > 0 ? (
            <p className="text-xs text-gray-400 text-center">Tous les distributeurs sont déjà associés.</p>
          ) : (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs text-gray-500 mb-1 block">Ajouter un distributeur</Label>
                <select value={newPartner} onChange={(e) => setNewPartner(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm" data-testid="add-dist-select">
                  <option value="">-- Choisir --</option>
                  {availablePartners.map((p) => (
                    <option key={p.id} value={p.id}>{p.nom}{p.franco ? ` (Franco: ${p.franco}\u20AC)` : ''}</option>
                  ))}
                </select>
              </div>
              <Button size="sm" onClick={handleAdd} disabled={!newPartner || adding} className="h-9" data-testid="add-dist-button">
                <Plus className="h-4 w-4 mr-1" /> {adding ? 'Ajout...' : 'Ajouter'}
              </Button>
            </div>
          )}
          {newPartner && isDuplicate(newPartner) && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600" data-testid="duplicate-error">
              <AlertCircle className="h-3.5 w-3.5" /> Ce distributeur est déjà associé.
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default AdminDistributionsManager;
